import './Modals.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, DatePicker, Grid, Input, Modal, Row, Spin } from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { AnnualPlanItem, ConfirmAction, ModalProps } from '../../utils/types';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { fetchData, Notify, postData } from '../../utils/constants';
import dayjs from 'dayjs';
import enGB from 'antd/es/date-picker/locale/en_GB';
import { apiClient } from '../../utils/httpClient';
import { parseDateOnly, toUtcDateString } from '../../utils/date';

type AnnualLeavePlanModalProps = ModalProps & {
  refreshData?: () => Promise<void>;
  year: number | string;
};

type PlanItemType = {
  p: AnnualPlanItem;
  isExisting: boolean;
  onUpdate: (
    id: number,
    field: keyof AnnualPlanItem,
    value: string | Date | boolean,
  ) => void;
  onDelete: (id: number) => void;
};

const AnnualLeavePlanModal = ({
  isOpen,
  setIsOpen,
  refreshData,
  year,
}: AnnualLeavePlanModalProps) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const [plan, setPlan] = useState<AnnualPlanItem[]>([]);
  const [initialPlan, setInitialPlan] = useState<AnnualPlanItem[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [onConfirmDelete, setOnConfirmDelete] = useState<ConfirmAction | null>(
    null,
  );
  const [isConfigUpdated, setIsConfigUpdated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [bankHolidayDates, setBankHolidayDates] = useState<Set<string>>(
    new Set(),
  );

  const existingPlanIds = useMemo(
    () => new Set(initialPlan.map((item) => item.id)),
    [initialPlan],
  );

  const fetchPlan = useCallback(async () => {
    try {
      setLoading(true);
      await fetchData(
        `${process.env.REACT_APP_API_URL}/leave-planning/getLeaveSchedules`,
        (data: AnnualPlanItem[]) => {
          setInitialPlan(data);
          setPlan(data);
          setIsConfigUpdated(false);
        },
      );
    } catch (error) {
      console.error('Error saving holidays:', error);
      Notify('error', 'Failed to save one or more holidays.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBankHolidays = useCallback(async () => {
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/holidays/getAllHolidays`,
        (data: { date: string | Date }[]) => {
          setBankHolidayDates(
            new Set(data.map((holiday) => toUtcDateString(parseDateOnly(holiday.date)))),
          );
        },
      );
    } catch (error) {
      console.error('Error fetching bank holidays:', error);
    }
  }, []);

  const countWeekdays = useCallback(
    (start: Date, end: Date): number => {
      let count = 0;
      const current = new Date(start);
      current.setHours(0, 0, 0, 0);
      end = new Date(end);
      end.setHours(0, 0, 0, 0);

      while (current <= end) {
        const day = current.getDay();
        if (day !== 0 && day !== 6 && !bankHolidayDates.has(toUtcDateString(current))) {
          count++;
        }
        current.setDate(current.getDate() + 1);
      }
      return count;
    },
    [bankHolidayDates],
  );

  const savePlan = useCallback(async () => {
    const existingDates = initialPlan.map(
      (p) => toUtcDateString(new Date(p.startDate)),
    );
    const newPlan = plan.filter(
      (p) =>
        !existingDates.includes(
          toUtcDateString(new Date(p.startDate)),
        ),
    );
    if (newPlan.length === 0) {
      Notify('info', 'No new dates to save.');
      return;
    }
    try {
      await Promise.all(
        newPlan.map((plan) => {
          const planToSave = {
            startDate: toUtcDateString(new Date(plan.startDate)),
            endDate: toUtcDateString(new Date(plan.endDate)),
            year,
          };
          return postData(
            `${process.env.REACT_APP_API_URL}/leave-planning/createLeaveSchedule`,
            planToSave,
          );
        }),
      );
      Notify('success', 'New plan saved successfully!');
      setIsConfigUpdated(false);
      await fetchPlan();
      if (refreshData) {
        await refreshData();
      }
    } catch (err) {
      console.error('Error saving plan:', err);
      Notify('error', 'Failed to save plan.');
    }
  }, [fetchPlan, initialPlan, plan, refreshData, year]);

  const handlePlanItemUpdate = useCallback(
    (
      id: number,
      field: keyof AnnualPlanItem,
      value: string | Date | boolean,
    ) => {
      setPlan((prev) =>
        prev.map((planItem) => {
          if (planItem.id !== id) return planItem;

          const isExisting = existingPlanIds.has(planItem.id);
          const updatedPlan = { ...planItem, [field]: value };

          if (!isExisting) {
            if (field === 'startDate') {
              const startDate = value as Date;
              const endDate = planItem.endDate;

              if (
                !endDate ||
                (endDate instanceof Date && endDate < startDate)
              ) {
                updatedPlan.endDate = startDate;
              }
            }
          }

          const start = updatedPlan.startDate as Date;
          const end = updatedPlan.endDate as Date;
          if (start && end && start <= end) {
            updatedPlan.numberOfDays = countWeekdays(start, end);
          } else {
            updatedPlan.numberOfDays = 0;
          }

          return updatedPlan;
        }),
      );
    },
    [countWeekdays, existingPlanIds],
  );

  const addAnnualPlanItem = () => {
    const newPlanItem: AnnualPlanItem = {
      id: Date.now(),
      startDate: new Date(),
      endDate: new Date(),
      year: Number(year),
      numberOfDays: 1,
    };
    setPlan((prev) => [...prev, newPlanItem]);
    setIsConfigUpdated(true);
  };

  const deletePlanItem = useCallback(
    async (id: number) => {
      const isExisting = existingPlanIds.has(id);

      if (isExisting) {
        try {
          await apiClient.delete(
            `${process.env.REACT_APP_API_URL}/leave-planning/deleteLeaveSchedule/${id}`,
          );
          await fetchPlan();
          Notify('success', 'Plan date deleted successfully.');
        } catch (error) {
          console.error('Error deleting plan date:', error);
          Notify('error', 'Failed to delete plan date.');
        }
      } else {
        setPlan((prev) => prev.filter((item) => item.id !== id));
        setIsConfigUpdated(true);
        Notify('info', 'New plan date removed.');
      }
    },
    [existingPlanIds, fetchPlan],
  );

  useEffect(() => {
    if (!isOpen) return;
    fetchPlan();
    fetchBankHolidays();
  }, [isOpen, fetchPlan, fetchBankHolidays]);

  const PlanItem = React.memo(
    ({ p, isExisting, onUpdate, onDelete }: PlanItemType) => (
      <Row key={p.id} gutter={[8, 8]} align="middle" style={{ marginBottom: 10 }}>
        <Col xs={12} sm={10}>
          <DatePicker
            value={p.startDate ? dayjs(p.startDate) : null}
            onChange={(date) =>
              !isExisting && date && onUpdate(p.id, 'startDate', date.toDate())
            }
            format="YYYY-MM-DD"
            locale={enGB}
            disabled={isExisting}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={12} sm={10}>
          <DatePicker
            value={p.endDate ? dayjs(p.endDate) : null}
            onChange={(date) =>
              !isExisting && date && onUpdate(p.id, 'endDate', date.toDate())
            }
            format="YYYY-MM-DD"
            locale={enGB}
            disabled={isExisting}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={12} sm={3}>
          <Input value={p.numberOfDays} readOnly disabled={isExisting} style={{ width: '100%' }} />
        </Col>
        <Col xs={12} sm={1}>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              setOnConfirmDelete(() => () => onDelete(p.id));
              setDeleteModalOpen(true);
            }}
            title="Delete date"
            block={isMobile}
          />
        </Col>
      </Row>
    ),
  );
  PlanItem.displayName = 'PlanItem';

  return (
    <>
      <Button
        title="Add new leave plan"
        className="new-leave-btn mr-2"
        onClick={() => setIsOpen(true)}
      >
        Add new leave plan
      </Button>
      <Modal
        open={isOpen}
        onCancel={() => {
          setIsOpen(false);
          setPlan([]);
          if (refreshData) refreshData();
        }}
        title="Create annual leave plan"
        width={isMobile ? 'calc(100vw - 24px)' : 760}
        centered
        footer={[
          <Button key="cancel" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            icon={<CheckOutlined />}
            onClick={savePlan}
            disabled={!isConfigUpdated}
          >
            Save plan
          </Button>,
        ]}
      >
        {!isMobile && (
          <Row gutter={[8, 8]} style={{ marginBottom: 10 }}>
            <Col sm={10}>
              <strong>Start date</strong>
            </Col>
            <Col sm={10}>
              <strong>End date</strong>
            </Col>
            <Col sm={3}>
              <strong>Days</strong>
            </Col>
          </Row>
        )}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Spin size="small" />
            <p>Loading annual plan...</p>
          </div>
        ) : plan.length === 0 ? (
          <p>No annual plan found</p>
        ) : (
          plan.map((p) => {
            const isExisting = existingPlanIds.has(p.id);

            return (
              <PlanItem
                key={p.id}
                p={p}
                isExisting={isExisting}
                onUpdate={handlePlanItemUpdate}
                onDelete={deletePlanItem}
              />
            );
          })
        )}
        <Button onClick={addAnnualPlanItem} type="primary" icon={<PlusOutlined />} block={isMobile}>
          Add new date
        </Button>

        {onConfirmDelete && (
          <DeleteConfirmationModal
            isOpen={deleteModalOpen}
            setIsOpen={setDeleteModalOpen}
            onCancel={() => setDeleteModalOpen(false)}
            onConfirm={() => {
              onConfirmDelete();
              setDeleteModalOpen(false);
            }}
          />
        )}
      </Modal>
    </>
  );
};

export default AnnualLeavePlanModal;
